import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Users, Calendar, DollarSign, Wrench, MapPin,
  Search, ChevronRight, Clock, AlertTriangle, CheckCircle,
  TrendingUp, Activity, CreditCard, Filter, RefreshCw,
  Plus, Edit, Trash2, X, Save, Package, Shield
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const SEGMENT_CONFIG = {
  vip: { label: 'VIP', color: '#fac775', bg: 'rgba(250,199,117,0.12)', border: 'rgba(250,199,117,0.25)' },
  regulier: { label: 'Régulier', color: '#00E01A', bg: 'rgba(0,224,26,0.1)', border: 'rgba(0,224,26,0.25)' },
  decouverte: { label: 'Découverte', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)' },
  inactif: { label: 'Inactif', color: '#8C8F94', bg: 'rgba(140,143,148,0.1)', border: 'rgba(140,143,148,0.25)' },
};

const STATUS_CONFIG = {
  'confirmé': { color: '#00E01A', bg: '#142915', icon: CheckCircle },
  'terminé': { color: '#00E01A', bg: '#142915', icon: CheckCircle },
  'annulé': { color: '#EF4444', bg: '#2A1515', icon: null },
  'en attente': { color: '#F59E0B', bg: '#2A2010', icon: Clock },
  'payé': { color: '#00E01A', bg: '#142915', icon: CheckCircle },
  'acompte': { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)', icon: CreditCard },
  'remboursé': { color: '#8C8F94', bg: 'rgba(140,143,148,0.1)', icon: null },
  'ouvert': { color: '#F59E0B', bg: '#2A2010', icon: AlertTriangle },
  'en_cours': { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)', icon: Activity },
  'résolu': { color: '#00E01A', bg: '#142915', icon: CheckCircle },
  'fermé': { color: '#8C8F94', bg: 'rgba(140,143,148,0.1)', icon: null },
};

function formatCurrency(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

const TABS = [
  { key: 'bookings', label: 'Réservations', icon: Calendar },
  { key: 'payments', label: 'Paiements', icon: DollarSign },
  { key: 'clients', label: 'Clients', icon: Users },
  { key: 'materiel', label: 'Matériel', icon: Package },
];

const Badge = ({ status }) => {
  const s = STATUS_CONFIG[status] || { color: '#8C8F94', bg: 'rgba(140,143,148,0.1)' };
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider" style={{ backgroundColor: s.bg, color: s.color }}>
      {s.icon && <s.icon size={10} />}
      {status}
    </span>
  );
};

export default function ExploitationSiteDetail() {
  const { siteId } = useParams();
  const { api } = useAuth();
  const [tab, setTab] = useState('bookings');
  const [site, setSite] = useState(null);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [materiel, setMateriel] = useState([]);
  const [materielTypes, setMaterielTypes] = useState([]);
  const [materielStatuses, setMaterielStatuses] = useState([]);
  const [showMaterielForm, setShowMaterielForm] = useState(false);
  const [editingMateriel, setEditingMateriel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [searchBooking, setSearchBooking] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [sitesRes, clientsRes, bookingsRes, paymentsRes, ticketsRes, materielRes, typesRes] = await Promise.all([
        api.get('/exploitation/sites'),
        api.get(`/exploitation/sites/${siteId}/clients`),
        api.get(`/exploitation/sites/${siteId}/bookings`),
        api.get(`/exploitation/sites/${siteId}/payments`),
        api.get(`/exploitation/sites/${siteId}/tickets`),
        api.get(`/exploitation/materiel?site_id=${siteId}`),
        api.get('/exploitation/materiel/types'),
      ]);
      const found = sitesRes.data.find(s => s.site_id === siteId);
      setSite(found || { name: siteId, city: '' });
      setClients(clientsRes.data);
      setBookings(bookingsRes.data);
      setPayments(paymentsRes.data);
      setTickets(ticketsRes.data);
      setMateriel(materielRes.data);
      setMaterielTypes(typesRes.data.types || []);
      setMaterielStatuses(typesRes.data.statuses || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement des données');
    } finally { setLoading(false); }
  }, [api, siteId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/exploitation/sync/${siteId}`);
      toast.success(res.data.message);
      setLastSync(res.data.last_sync);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur synchronisation');
    } finally { setSyncing(false); }
  };

  // Derived data
  const totalCA = useMemo(() =>
    payments.filter(p => ['payé', 'acompte'].includes(p.payment_status)).reduce((s, p) => s + (p.amount || 0), 0),
  [payments]);

  const pendingAmount = useMemo(() =>
    payments.filter(p => p.payment_status === 'en attente').reduce((s, p) => s + (p.amount || 0), 0),
  [payments]);

  const openTickets = useMemo(() =>
    tickets.filter(t => ['ouvert', 'en_cours'].includes(t.status)),
  [tickets]);

  const activities = useMemo(() =>
    [...new Set(bookings.map(b => b.activity_type).filter(Boolean))],
  [bookings]);

  const filteredBookings = useMemo(() =>
    bookings.filter(b => {
      if (statusFilter && b.status !== statusFilter) return false;
      if (activityFilter && b.activity_type !== activityFilter) return false;
      if (searchBooking) {
        const q = searchBooking.toLowerCase();
        return (b.client_name?.toLowerCase().includes(q) || b.activity_type?.toLowerCase().includes(q));
      }
      return true;
    }),
  [bookings, statusFilter, activityFilter, searchBooking]);

  const filteredPayments = useMemo(() =>
    payments.filter(p => !paymentStatusFilter || p.payment_status === paymentStatusFilter),
  [payments, paymentStatusFilter]);

  // Activity breakdown
  const activityStats = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      const a = b.activity_type || 'Autre';
      if (!map[a]) map[a] = { count: 0, revenue: 0 };
      map[a].count++;
      map[a].revenue += b.amount || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].count - a[1].count);
  }, [bookings]);

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[50vh]"><div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exploitation-site-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/exploitation" className="mt-1 p-2 rounded-lg text-[#8C8F94] hover:text-white hover:bg-[#1D2327] transition-colors" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-12 h-12 rounded-xl bg-[#00E01A]/10 flex items-center justify-center border border-[#00E01A]/20">
              <MapPin size={22} className="text-[#00E01A]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {site?.name || siteId}
              </h1>
              <p className="text-[#8C8F94] text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
                {site?.address || site?.city}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full text-xs transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,224,26,0.2)]"
          data-testid="sync-site-btn"
        >
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Synchronisation...' : 'Synchroniser les réservations'}
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="site-stats">
        {[
          { label: 'CA encaissé', value: formatCurrency(totalCA), color: '#00E01A', icon: TrendingUp },
          { label: 'En attente', value: formatCurrency(pendingAmount), color: '#F59E0B', icon: Clock },
          { label: 'Réservations', value: bookings.length, color: '#3B82F6', icon: Calendar },
          { label: 'Clients', value: clients.length, color: '#FFFFFF', icon: Users },
          { label: 'SAV ouverts', value: openTickets.length, color: openTickets.length > 0 ? '#EF4444' : '#00E01A', icon: Wrench },
        ].map(s => (
          <div key={s.label} className="bg-[#111111] rounded-xl border border-[#222] p-3.5 flex items-center gap-3 hover:border-[#333] transition-colors">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '12' }}>
              <s.icon size={16} style={{ color: s.color }} />
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold text-white truncate" style={{ fontFamily: "'Bebas Neue', sans-serif", color: s.color === '#FFFFFF' ? '#fff' : s.color }}>{s.value}</p>
              <p className="text-[9px] uppercase tracking-wider text-[#8C8F94] truncate" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Activity breakdown - mini bar */}
      {activityStats.length > 0 && (
        <div className="bg-[#111111] rounded-xl border border-[#222] p-4" data-testid="activity-breakdown">
          <h3 className="text-[#8C8F94] text-xs uppercase tracking-wider mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Répartition par activité</h3>
          <div className="flex gap-2 flex-wrap">
            {activityStats.map(([name, stat]) => {
              const pct = bookings.length > 0 ? Math.round(stat.count / bookings.length * 100) : 0;
              return (
                <button
                  key={name}
                  onClick={() => { setTab('bookings'); setActivityFilter(activityFilter === name ? '' : name); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                    activityFilter === name
                      ? 'border-[#00E01A] bg-[#00E01A]/10 text-[#00E01A]'
                      : 'border-[#222] bg-[#0a0a0a] text-white hover:border-[#333]'
                  }`}
                  data-testid={`activity-${name}`}
                >
                  <span className="font-medium">{name}</span>
                  <span className="text-[#52555A] text-xs">{stat.count} ({pct}%)</span>
                  <span className="text-[#00E01A] text-xs font-semibold">{formatCurrency(stat.revenue)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0a0a0a] rounded-xl p-1 border border-[#222]" data-testid="site-tabs">
        {TABS.map(t => {
          const count = t.key === 'bookings' ? filteredBookings.length
            : t.key === 'payments' ? filteredPayments.length
            : t.key === 'clients' ? clients.length
            : tickets.length;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                tab === t.key
                  ? 'bg-[#00E01A] text-black shadow-[0_0_12px_rgba(0,224,26,0.15)]'
                  : 'text-[#8C8F94] hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon size={16} />
              <span className="hidden sm:inline">{t.label}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                tab === t.key ? 'bg-black/20 text-black' : 'bg-white/10 text-[#8C8F94]'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* BOOKINGS TAB */}
        {tab === 'bookings' && (
          <div className="space-y-3" data-testid="tab-content-bookings">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52555A]" />
                <Input value={searchBooking} onChange={e => setSearchBooking(e.target.value)} placeholder="Rechercher client ou activité..."
                  className="pl-8 h-9 bg-[#111111] border-[#222] text-white text-sm placeholder:text-[#52555A]" data-testid="booking-search" />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="bg-[#111111] border border-[#222] text-white rounded-lg px-3 py-2 text-sm focus:border-[#00E01A] outline-none" data-testid="booking-status-filter">
                <option value="">Tous statuts</option>
                <option value="confirmé">Confirmé</option>
                <option value="en attente">En attente</option>
                <option value="terminé">Terminé</option>
                <option value="annulé">Annulé</option>
              </select>
              <select value={activityFilter} onChange={e => setActivityFilter(e.target.value)}
                className="bg-[#111111] border border-[#222] text-white rounded-lg px-3 py-2 text-sm focus:border-[#00E01A] outline-none" data-testid="booking-activity-filter">
                <option value="">Toutes activités</option>
                {activities.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              {(statusFilter || activityFilter || searchBooking) && (
                <button onClick={() => { setSearchBooking(''); setStatusFilter(''); setActivityFilter(''); }}
                  className="text-[#8C8F94] hover:text-white text-xs px-2 py-1 rounded bg-[#1D2327]" data-testid="clear-booking-filters">Effacer</button>
              )}
            </div>

            {filteredBookings.length === 0 ? (
              <EmptyState icon={Calendar} text="Aucune réservation trouvée" />
            ) : (
              <div className="space-y-2">
                {filteredBookings.map(b => (
                  <div key={b.id} className="bg-[#111111] rounded-xl border border-[#222] p-4 hover:border-[#333] transition-colors group" data-testid={`booking-${b.id}`}>
                    <div className="flex items-center gap-4">
                      {/* Date block */}
                      <div className="w-14 h-14 rounded-xl bg-[#0a0a0a] border border-[#222] flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[#00E01A] text-xs font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                          {b.date ? new Date(b.date).toLocaleDateString('fr-FR', { month: 'short' }) : ''}
                        </span>
                        <span className="text-white text-lg font-bold leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                          {b.date ? new Date(b.date).getDate() : ''}
                        </span>
                      </div>

                      {/* Main info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-semibold text-sm truncate">{b.client_name || 'Client inconnu'}</h4>
                          <Badge status={b.status} />
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#8C8F94]">
                          <span className="flex items-center gap-1"><Activity size={11} /> {b.activity_type}</span>
                          {b.duration_minutes > 0 && <span className="flex items-center gap-1"><Clock size={11} /> {b.duration_minutes >= 60 ? `${Math.floor(b.duration_minutes/60)}h${b.duration_minutes%60>0?(b.duration_minutes%60+'min'):''}` : `${b.duration_minutes}min`}</span>}
                          {b.time_slot && <span>{b.time_slot}</span>}
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-white font-bold text-base" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatCurrency(b.amount)}</p>
                        {b.payment_status && <Badge status={b.payment_status} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PAYMENTS TAB */}
        {tab === 'payments' && (
          <div className="space-y-3" data-testid="tab-content-payments">
            {/* Payment summary cards */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Encaissé', amount: totalCA, color: '#00E01A', count: payments.filter(p => ['payé', 'acompte'].includes(p.payment_status)).length },
                { label: 'En attente', amount: pendingAmount, color: '#F59E0B', count: payments.filter(p => p.payment_status === 'en attente').length },
                { label: 'Total', amount: payments.reduce((s, p) => s + (p.amount || 0), 0), color: '#FFFFFF', count: payments.length },
              ].map(s => (
                <div key={s.label} className="bg-[#111111] rounded-xl border border-[#222] p-4 text-center">
                  <p className="text-xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", color: s.color }}>{formatCurrency(s.amount)}</p>
                  <p className="text-[9px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{s.label} ({s.count})</p>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <select value={paymentStatusFilter} onChange={e => setPaymentStatusFilter(e.target.value)}
                className="bg-[#111111] border border-[#222] text-white rounded-lg px-3 py-2 text-sm focus:border-[#00E01A] outline-none" data-testid="payment-status-filter">
                <option value="">Tous statuts</option>
                <option value="payé">Payé</option>
                <option value="acompte">Acompte</option>
                <option value="en attente">En attente</option>
                <option value="remboursé">Remboursé</option>
              </select>
              {paymentStatusFilter && (
                <button onClick={() => setPaymentStatusFilter('')} className="text-[#8C8F94] hover:text-white text-xs px-2 py-1 rounded bg-[#1D2327]">Effacer</button>
              )}
            </div>

            {filteredPayments.length === 0 ? (
              <EmptyState icon={DollarSign} text="Aucun paiement trouvé" />
            ) : (
              <div className="space-y-2">
                {filteredPayments.map(p => (
                  <div key={p.id} className="bg-[#111111] rounded-xl border border-[#222] p-4 hover:border-[#333] transition-colors" data-testid={`payment-${p.id}`}>
                    <div className="flex items-center gap-4">
                      {/* Date */}
                      <div className="w-14 h-14 rounded-xl bg-[#0a0a0a] border border-[#222] flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[#3B82F6] text-xs font-bold uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                          {p.date ? new Date(p.date).toLocaleDateString('fr-FR', { month: 'short' }) : ''}
                        </span>
                        <span className="text-white text-lg font-bold leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                          {p.date ? new Date(p.date).getDate() : ''}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-semibold text-sm truncate">{p.client_name || 'Client inconnu'}</h4>
                        <p className="text-[#8C8F94] text-xs truncate">{p.product}</p>
                      </div>

                      {/* Amount & status */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-white font-bold text-base mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatCurrency(p.amount)}</p>
                        <Badge status={p.payment_status} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CLIENTS TAB */}
        {tab === 'clients' && (
          <div className="space-y-3" data-testid="tab-content-clients">
            {clients.length === 0 ? (
              <EmptyState icon={Users} text="Aucun client sur ce site" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {clients.map(client => {
                  const seg = SEGMENT_CONFIG[client.segment] || SEGMENT_CONFIG.decouverte;
                  return (
                    <Link
                      key={client.id}
                      to={`/exploitation/clients/${client.id}`}
                      className="bg-[#111111] rounded-xl border border-[#222] p-4 hover:border-[#00E01A]/30 transition-all group flex items-center gap-4"
                      data-testid={`site-client-${client.id}`}
                    >
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                        style={{ backgroundColor: seg.bg, color: seg.color, border: `1.5px solid ${seg.border}` }}>
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-semibold text-sm truncate group-hover:text-[#00E01A] transition-colors">
                            {client.first_name} {client.last_name}
                          </h4>
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase" style={{ backgroundColor: seg.bg, color: seg.color }}>
                            {seg.label}
                          </span>
                        </div>
                        <p className="text-[#52555A] text-xs truncate">{client.email}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[#00E01A] font-bold text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatCurrency(client.total_spent_live || 0)}</p>
                        <p className="text-[#52555A] text-[10px]">{client.total_bookings_live || 0} résa</p>
                      </div>
                      <ChevronRight size={16} className="text-[#52555A] group-hover:text-[#00E01A] transition-colors flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SAV TAB */}
        {tab === 'materiel' && (
          <MaterielTab
            materiel={materiel}
            types={materielTypes}
            statuses={materielStatuses}
            siteId={siteId}
            api={api}
            onRefresh={fetchAll}
            showForm={showMaterielForm}
            setShowForm={setShowMaterielForm}
            editing={editingMateriel}
            setEditing={setEditingMateriel}
          />
        )}
      </div>
    </div>
  );
}

function MaterielTab({ materiel, types, statuses, siteId, api, onRefresh, showForm, setShowForm, editing, setEditing }) {
  const [form, setForm] = useState({ name: '', type: 'board', serial_number: '', brand: '', model: '', site_id: siteId, status: 'bon', purchase_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  const startEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, type: item.type, serial_number: item.serial_number || '', brand: item.brand || '', model: item.model || '', site_id: item.site_id, status: item.status, purchase_date: item.purchase_date || '', notes: item.notes || '' });
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ name: '', type: 'board', serial_number: '', brand: '', model: '', site_id: siteId, status: 'bon', purchase_date: '', notes: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/exploitation/materiel/${editing.id}`, form);
        toast.success("Matériel mis à jour");
      } else {
        await api.post('/exploitation/materiel', form);
        toast.success("Matériel ajouté");
      }
      resetForm();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce matériel ?")) return;
    try { await api.delete(`/exploitation/materiel/${id}`); toast.success("Supprimé"); onRefresh(); }
    catch { toast.error("Erreur suppression"); }
  };

  const filtered = typeFilter ? materiel.filter(m => m.type === typeFilter) : materiel;
  const statusMap = {};
  statuses.forEach(s => { statusMap[s.id] = s; });
  const typeMap = {};
  types.forEach(t => { typeMap[t.id] = t; });

  // Stats
  const byStatus = {};
  materiel.forEach(m => { byStatus[m.status] = (byStatus[m.status] || 0) + 1; });

  const inputClass = "w-full bg-[#0a0a0a] border border-[#222] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none transition-colors";
  const labelClass = "text-[#8C8F94] text-[10px] uppercase tracking-wider mb-1 block";

  return (
    <div className="space-y-4" data-testid="tab-content-materiel">
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statuses.map(s => (
          <div key={s.id} className="bg-[#111111] rounded-xl border border-[#222] p-3 flex items-center gap-3">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
            <div>
              <p className="text-lg font-bold text-white">{byStatus[s.id] || 0}</p>
              <p className="text-[10px] text-[#8C8F94] uppercase">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <select className="bg-[#111111] border border-[#222] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00E01A] focus:outline-none"
          value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="materiel-type-filter">
          <option value="">Tous les types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2 rounded-full text-sm transition-all shadow-[0_0_12px_rgba(0,224,26,0.3)]"
          data-testid="add-materiel-btn">
          <Plus size={16} /> Ajouter du matériel
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[#111111] rounded-xl border border-[#00E01A]/20 p-5 space-y-3" data-testid="materiel-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold text-sm">{editing ? 'Modifier' : 'Ajouter'} du matériel</h4>
            <button onClick={resetForm} className="text-[#8C8F94] hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className={labelClass}>Nom *</label><input className={inputClass} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="materiel-name" /></div>
            <div><label className={labelClass}>Type</label>
              <select className={inputClass} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} data-testid="materiel-type">
                {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div><label className={labelClass}>Statut</label>
              <select className={inputClass} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} data-testid="materiel-status">
                {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div><label className={labelClass}>Marque</label><input className={inputClass} value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} data-testid="materiel-brand" /></div>
            <div><label className={labelClass}>Modèle</label><input className={inputClass} value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} data-testid="materiel-model" /></div>
            <div><label className={labelClass}>N° série</label><input className={inputClass} value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} data-testid="materiel-serial" /></div>
            <div><label className={labelClass}>Date d'achat</label><input className={inputClass} type="date" value={form.purchase_date} onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} /></div>
          </div>
          <div><label className={labelClass}>Notes</label><textarea className={inputClass + " min-h-[60px]"} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="materiel-notes" /></div>
          <button onClick={handleSubmit} disabled={saving}
            className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-5 py-2 rounded-full text-sm transition-all disabled:opacity-50"
            data-testid="materiel-save-btn">
            {saving ? <Clock size={14} className="animate-spin" /> : <Save size={14} />}
            {editing ? 'Mettre à jour' : 'Ajouter'}
          </button>
        </div>
      )}

      {/* Equipment list */}
      {filtered.length === 0 ? (
        <EmptyState icon={Package} text="Aucun matériel enregistré pour ce site" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(item => {
            const st = statusMap[item.status] || { label: item.status, color: '#8C8F94' };
            const tp = typeMap[item.type] || { label: item.type };
            return (
              <div key={item.id} className="bg-[#111111] rounded-xl border border-[#222] p-4 hover:border-[#333] transition-all" data-testid={`materiel-${item.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0a0a0a] flex items-center justify-center border border-[#222]">
                      <Package size={18} className="text-[#8C8F94]" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{item.name}</h4>
                      <p className="text-[#8C8F94] text-xs">{tp.label}{item.brand ? ` — ${item.brand}` : ''}{item.model ? ` ${item.model}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase" style={{ backgroundColor: `${st.color}15`, color: st.color }}>
                      {st.label}
                    </span>
                    <button onClick={() => startEdit(item)} className="p-1.5 text-[#8C8F94] hover:text-[#00E01A] transition-colors" data-testid={`edit-materiel-${item.id}`}><Edit size={14} /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-[#8C8F94] hover:text-[#EF4444] transition-colors" data-testid={`delete-materiel-${item.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#52555A] mt-2">
                  {item.serial_number && <span className="font-mono">S/N: {item.serial_number}</span>}
                  {item.purchase_date && <span>{formatDate(item.purchase_date)}</span>}
                </div>
                {item.notes && <p className="text-[#8C8F94] text-xs mt-2 line-clamp-2">{item.notes}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="bg-[#111111] rounded-xl border border-[#222] p-12 text-center">
      <Icon size={36} className="mx-auto text-[#52555A] mb-3" />
      <p className="text-[#8C8F94]">{text}</p>
    </div>
  );
}
